import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Input, Modal, Typography } from 'antd';
// Api
import { REPLACE_CAMPAIGN_SUBMISSION_LINK } from 'api/campaignV2/mutations';
// Types
import {
  ReplaceCampaignSubmissionLink,
  ReplaceCampaignSubmissionLinkVariables,
} from 'api/campaignV2/types/ReplaceCampaignSubmissionLink';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './ReplaceSubmissionLink.module.scss';

const { Text } = Typography;

type ReplaceSubmissionLinkProps = {
  submissionId: string;
  currentLink?: string | null;
};

const ReplaceSubmissionLink: React.FC<ReplaceSubmissionLinkProps> = ({
  submissionId,
  currentLink,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [link, setLink] = useState<string>(currentLink ?? '');

  const [replaceCampaignSubmissionLink, { loading }] = useMutation<
    ReplaceCampaignSubmissionLink,
    ReplaceCampaignSubmissionLinkVariables
  >(REPLACE_CAMPAIGN_SUBMISSION_LINK);

  const trimmedLink = link.trim();
  const isSaveDisabled = !trimmedLink || trimmedLink === currentLink;

  const handleOpen = () => {
    setLink(currentLink ?? '');
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSave = async () => {
    if (isSaveDisabled || loading) {
      return;
    }

    try {
      await replaceCampaignSubmissionLink({
        variables: { input: { submissionId, link: trimmedLink } },
      });
      successNotification(
        'Link replaced. Metrics will refresh once the new post is scraped.'
      );
      setIsOpen(false);
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  return (
    <>
      <Button size="small" onClick={handleOpen}>
        Replace link
      </Button>
      <Modal
        title="Replace submitted link"
        visible={isOpen}
        onCancel={handleClose}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ disabled: isSaveDisabled, loading }}
        destroyOnClose
      >
        <div className={styles.content}>
          <Text type="secondary">
            Paste the full post link. Saving replaces the creator&apos;s
            submitted link and re-fetches its metrics.
          </Text>
          <Input
            aria-label="New post link"
            placeholder="https://www.tiktok.com/@username/video/1234567890"
            value={link}
            onChange={(event) => setLink(event.target.value)}
            onPressEnter={handleSave}
          />
        </div>
      </Modal>
    </>
  );
};

export default ReplaceSubmissionLink;
